// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceFileManagementService, HceResponseType } from '../../hce-file-management/hce-file-management.service';
import { Observable, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { FileMeta } from '../../hce-api/hce-api.service';
import { Analysis } from '../../../../interfaces/analysis';
import { HceAlignmentData } from '../../../../interfaces/hce/hce-alignment-data';
import { Utils } from '../../hce-parser/utils';
import { HceAnalysisMetadataService } from '../hce-analysis-metadata/hce-analysis-metadata.service';

@Injectable({ providedIn: 'root' })
export class HceAnalysisContentService {

  // ===== Constructor & DI =====
  constructor(
    private readonly file: HceFileManagementService,
    private readonly metadata: HceAnalysisMetadataService,
  ) {}

  // ===== Public API: Content Retrieval =====

  // -- Assembly (asm)
  public getAsmUnusedInitMaps2dHist<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/unused_init_maps_2d_hist.tsv', responseType);
  }
  public getAsmUnusedMaps2dHist<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/unused_maps_2d_hist.tsv', responseType);
  }
  public getAsmUsedInitMaps2dHist<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/used_init_maps_2d_hist.tsv', responseType);
  }
  public getAsmUsedMaps2dHist<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/used_maps_2d_hist.tsv', responseType);
  }
  public getAsmNabsys<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/assembly/nabsys.zip', responseType);
  }
  public getAsmContig<T extends HceResponseType = 'text'>(analysisId: number, contigId: number, responseType?: T) {
    return this.getFile(analysisId, `asm_results/output/assembly/gajson/contigs/${contigId}.json.gz`, responseType);
  }
  public getAsmContigJson<T extends object = any>(analysisId: number, contigId: number): Observable<T> {
    return this.getAsmContig(analysisId, contigId).pipe(map(text => this.parseJson(text)));
  }
  public getAsmParams<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'asm_results/output/config/assembly_params.json', responseType);
  }
  public getAsmParamsJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getAsmParams(analysisId).pipe(map(text => this.parseJson(text)));
  }

  // -- Input
  public getInput<T extends HceResponseType = 'text'>(analysis: Analysis, filename: string, responseType?: T) {
    return this.getFile(analysis.AnalysisID, filename, responseType);
  }

  // -- Map2Ref
  public getMap2refGenderPrediction<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'map2ref/gender_prediction.json', responseType);
  }
  public getMap2refGenderPredictionJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getMap2refGenderPrediction(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getMap2refGappedAlignerParams<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'map2ref/config/gapped_aligner_params.json', responseType);
  }
  public getMap2refGappedAlignerParamsJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getMap2refGappedAlignerParams(analysisId).pipe(map(text => this.parseJson(text)));
  }

  // -- QC
  public getQcStats0<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'qc_results/stats0_results.json', responseType);
  }
  public getQcStats0Json<T extends object = any>(analysisId: number): Observable<T> {
    return this.getQcStats0(analysisId).pipe(map(text => this.parseJson(text)));
  }

  // -- SV
  public getSvAlignedAsmStats<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/aligned_assembly_stats.json', responseType);
  }
  public getSvAlignedAsmStatsJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getSvAlignedAsmStats(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedCoverage<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/enhanced.coverage.txt', responseType);
  }
  public getSvEnhancedHsv<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/enhanced.hsv.json', responseType);
  }
  public getSvEnhancedHsvJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getSvEnhancedHsv(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedMultihits<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/enhanced.multihits.json', responseType);
  }
  public getSvEnhancedMultihitsJson(analysisId: number): Observable<Record<string, Record<string, number[]>>> {
    return this.getSvEnhancedMultihits(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedRefGene<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/enhanced.ref_gene.txt', responseType);
  }
  public getSvDetectionConfig<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/sv_detection_config.json', responseType);
  }
  public getSvDetectionConfigJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getSvDetectionConfig(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getSvGappedAlignerParams<T extends HceResponseType = 'text'>(analysisId: number, responseType?: T) {
    return this.getFile(analysisId, 'sv/results/config/gapped_aligner_params.json', responseType);
  }
  public getSvGappedAlignerParamsJson<T extends object = any>(analysisId: number): Observable<T> {
    return this.getSvGappedAlignerParams(analysisId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedContigs<T extends HceResponseType = 'text'>(analysis: Analysis, chromosomeId: number, responseType: T = 'text' as T) {
    return this.getSvEnhancedChr(analysis, chromosomeId, responseType, 'contigs', (...args) => this.metadata.getSvEnhancedContigsFilesMeta(...args));
  }
  public getSvEnhancedContigsJson<T extends object = any>(analysis: Analysis, chromosomeId: number): Observable<T> {
    return this.getSvEnhancedContigs(analysis, chromosomeId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedContigsAll(analysis: Analysis): Observable<Record<number, HceAlignmentData>> {
    return this.getSvEnhancedChrAll(analysis, 'contigs', analysis => this.metadata.getSvEnhancedContigsFilesMeta(analysis));
  }
  public getSvEnhancedGappedAlns<T extends HceResponseType = 'text'>(analysis: Analysis, chromosomeId: number, responseType: T = 'text' as T) {
    return this.getSvEnhancedChr(analysis, chromosomeId, responseType, 'gapped_alns', (...args) => this.metadata.getSvEnhancedGappedAlnsFilesMeta(...args));
  }
  public getSvEnhancedGappedAlnsJson<T extends object = any>(analysis: Analysis, chromosomeId: number): Observable<T> {
    return this.getSvEnhancedGappedAlns(analysis, chromosomeId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedGappedAlnsAll(analysis: Analysis): Observable<Record<number, HceAlignmentData>> {
    return this.getSvEnhancedChrAll(analysis, 'gapped_alns', analysis => this.metadata.getSvEnhancedGappedAlnsFilesMeta(analysis));
  }
  public getSvEnhancedScaled<T extends HceResponseType = 'text'>(analysis: Analysis, chromosomeId: number, responseType: T = 'text' as T) {
    return this.getSvEnhancedChr(analysis, chromosomeId, responseType, 'scaled', (...args) => this.metadata.getSvEnhancedScaledFilesMeta(...args));
  }
  public getSvEnhancedScaledJson<T extends object = any>(analysis: Analysis, chromosomeId: number): Observable<T> {
    return this.getSvEnhancedScaled(analysis, chromosomeId).pipe(map(text => this.parseJson(text)));
  }
  public getSvEnhancedScaledAll(analysis: Analysis): Observable<Record<number, HceAlignmentData>> {
    return this.getSvEnhancedChrAll(analysis, 'scaled', analysis => this.metadata.getSvEnhancedScaledFilesMeta(analysis));
  }

  // ===== Private helpers: Content helpers =====
  private getFile<T extends HceResponseType>(
    analysisId: number,
    filename: string,
    responseType: T = 'text' as T
  ): Observable<
    T extends 'arraybuffer'
      ? ArrayBuffer
      : T extends 'blob'
        ? Blob
        : string
  > {
    return this.file.getAnalysisFile(analysisId, filename, responseType) as any;
  }

  private parseJson<T extends object>(
    text: string,
    fallback: T = {} as T,
  ): T {
    try {
      const v = JSON.parse(text);
      return (v && typeof v === 'object') ? (v as T) : fallback;
    } catch {
      return fallback;
    }
  }

  private getSvEnhancedChr<T extends HceResponseType>(
    analysis: Analysis,
    chromosomeId: number,
    responseType: T,
    type: 'contigs' | 'gapped_alns' | 'scaled',
    fileMetaResolver: (analysis: Analysis, pattern?: RegExp) => Observable<FileMeta[]>
  ): Observable<
    T extends 'arraybuffer'
      ? ArrayBuffer
      : T extends 'blob'
        ? Blob
        : string
  > {
    const pattern = new RegExp(`^(?:chr)?${chromosomeId}\\.json$`, 'i');
    return fileMetaResolver(analysis, pattern).pipe(
      switchMap(files => {
        if (files.length === 0) {
          const message = `SV enhanced ${type} file not found for chromosome ${chromosomeId}`;
          return throwError(() => new Error(message));
        }
        if (files.length > 1) {
          const candidates = files.map(f => f.filename).join(', ');
          const message = `Multiple SV enhanced ${type} file found for chromosome ${chromosomeId}: ${candidates}`;
          return throwError(() => new Error(message));
        }
        const relativePath = `sv/results/enhanced.${type}/${files[0].filename}`;
        return this.getFile(analysis.AnalysisID, relativePath, responseType);
      })
    );
  }

  private getSvEnhancedChrAll(
    analysis: Analysis,
    type: 'contigs' | 'gapped_alns' | 'scaled',
    fileMetaResolver: (analysis: Analysis, pattern?: RegExp) => Observable<FileMeta[]>
  ): Observable<Record<number, HceAlignmentData>> {
    return fileMetaResolver(analysis).pipe(
      switchMap(files => {
        const source: Record<number, Observable<HceAlignmentData>> = {};
        for (const file of files) {
          const chromosomeId = this.toChromosomeId(file.filename);
          if (chromosomeId == null) continue;
          const relativePath = `sv/results/enhanced.${type}/${file.filename}`;
          source[chromosomeId] = this.getFile(analysis.AnalysisID, relativePath, 'text').pipe(
            map(text => this.parseJson(text))
          );
        }
        return Object.keys(source).length === 0
          ? of({} as Record<number, HceAlignmentData>)
          : forkJoin(source);
      })
    )
  }

  // ====== Private helpers: Utils =====
  private toChromosomeId(filename: string): number | null {
    const m = filename.match(/(\d+)\.json$/i);
    if (m?.length === 2) {
      const n = Number(m[1]);
      return Number.isInteger(n) ? n : null;
    }
    return Utils.chromosome.toId(filename);
  }
}
